import { useEffect, useRef } from 'react'
import { hydrateMailImages } from '../lib/hydrateMailImages'
import { forceExternalLinksNewTab } from '../lib/sanitize'

type AttMeta = {
  content_id?: string | null
  content_type?: string
  saved?: boolean
  filename?: string
}

type Props = {
  html: string
  messageId: string
  attachments?: AttMeta[] | null
  loadRemote: boolean
  className?: string
  title?: string
}

/**
 * Email body in normal page flow (div.mail-mailtext).
 * Blob iframe abandoned: same-origin blob inherits parent CSP style-src 'self',
 * which blocked every newsletter style → tiny broken pane + 1000+ console errors.
 * Inline styles are stripped by sanitize; app typography + table layout remain.
 */
export function MailBodyFrame({
  html,
  messageId,
  attachments,
  loadRemote,
  className,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const attKey = (attachments || [])
    .map((a) => `${a.content_id || ''}:${a.saved ? 1 : 0}`)
    .join('|')

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    root.innerHTML = html || ''
    if (!html || !messageId) return

    forceExternalLinksNewTab(root)

    const ac = new AbortController()
    void hydrateMailImages(root, {
      messageId,
      attachments: attachments || [],
      loadRemote,
      signal: ac.signal,
    })
    return () => ac.abort()
  }, [html, messageId, loadRemote, attKey, attachments])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    function onClick(e: MouseEvent) {
      const t = e.target
      if (!(t instanceof Element)) return
      const a = t.closest('a')
      if (!a || !root!.contains(a)) return
      const href = (a.getAttribute('href') || '').trim()
      if (!href || href.startsWith('#') || /^\s*javascript:/i.test(href)) {
        if (/^\s*javascript:/i.test(href)) e.preventDefault()
        return
      }
      if (/^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('//')) {
        e.preventDefault()
        window.open(a.href, '_blank', 'noopener,noreferrer')
      }
    }
    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [html, messageId])

  return (
    <div
      ref={rootRef}
      className={`mail-mailtext${className ? ` ${className}` : ''}`}
    />
  )
}
